/**
 * Consolidated unit-frame stack (#138) — a stable, always-visible roster of
 * compact frames for EVERY friendly + enemy player, grouped in a fixed screen
 * region (default: bottom of screen, friendlies LEFT / enemies RIGHT), like WoW
 * arena/party frames. Independent of world position, so a unit off-camera still
 * has a frame.
 *
 * Each frame shows: name / self label, a health bar, a cast bar (#420,
 * {@link FrameCastBar}), the direct-select TARGET HOTKEY (#136, reusing
 * `entityNameplate`), and — composing #137 — the curated MAJOR-aura row
 * ({@link MajorAuraRow}). It reads the sampled HUD snapshot (`WireSimState`)
 * + the live keymap; nothing new off the wire (mirrors how the #192 frame
 * cast bars pulled from the wire).
 *
 * PURELY COSMETIC / CLIENT-SIDE. No sim, no protocol, no hash, no SIM_VERSION.
 *
 * Grouping: an entity is FRIENDLY when its team equals the viewer's team (from
 * the viewer's own entity), else ENEMY — a stable, liveness-independent split so
 * a dead ally keeps its frame (dimmed). Within each group, frames sort by seat
 * (a canonical, stable order). The hotkey resolves via the #136 roster helpers
 * (self → none; dead/unbound → none).
 *
 * Degraded grouping when the viewer's team is UNKNOWN (#744): `ownEntity`
 * returns `null` — and so does the derived team — whenever `ownSeat` is
 * `null` (the match creator never receives a wired seat pre-#742) or the
 * seat doesn't resolve to a live entity in this snapshot (e.g. a spectator).
 * Splitting team-relative with an unknown team would classify EVERY entity
 * (including the viewer's own) as ENEMY — a confidently wrong "everyone is
 * hostile" answer. Instead, an unknown team renders a single neutral group:
 * every entity is placed in `friendlies` and `enemies` stays empty. This is
 * still wrong in the sense that real enemies are shown un-flagged, but it
 * fails toward "unlabeled roster" rather than "universal hostility," and it
 * is the option that never mislabels the viewer's own frame.
 *
 * Champion color + cast bar (#419/#420): every frame's label carries THAT
 * ENTITY'S OWN champion identity color (`championColorCss(e.championId)`),
 * additive to the team-color border — same convention `OwnFrame` (#140)
 * uses. Its cast bar/spell name likewise resolves against `e.championId` —
 * the wire's per-entity champion content-key (#420) closed the gap where
 * only the viewer's OWN champion was resolvable client-side.
 *
 * data-* contract (component + e2e assertions):
 *   • data-testid="unit-frame-stack" — always present when enabled
 *   • data-friendly-count / data-enemy-count — group sizes
 *   • group wrappers data-testid="unit-frame-group-friendly" / "-enemy"
 *   • each frame data-testid="unit-frame" (data-seat / data-faction / data-self /
 *     data-hp / data-max-hp / data-hotkey / data-alive / data-champion-color —
 *     this entity's identity accent)
 *   • each frame composes a <MajorAuraRow testId="unit-frame-aura-row"> and a
 *     <FrameCastBar testIdPrefix="unit-frame"> (bar/name omitted when idle)
 */

import type { CSSProperties, ReactElement } from "react";
import type { WireSimState } from "@pit/protocol";
import { useStore } from "../store/context.js";
import { useKeymap, type Keymap } from "../input/keybinding/index.js";
import { loadHudPrefs } from "../prefs.js";
import { shieldAmount, ownEntity } from "./hud-util.js";
import { entityNameplate } from "../render/nameplate.js";
import type { AuraCategory } from "../champions/auraIcons.js";
import { championColorCss } from "../champions/championColors.js";
import { FrameCastBar } from "./FrameCastBar.js";
import { HpBar } from "./HpBar.js";
import { unitFrameStyle } from "./unitFrameScale.js"
import {
  MajorAuraRow,
  DEFAULT_MAJOR_AURA_ROW_STYLE,
  type MajorAuraRowStyle,
} from "./MajorAuraRow.js";

type WireEntity = WireSimState["entities"][number];

/** Which team-relative group a frame belongs to (drives side + accent). */
export type UnitFaction = "friendly" | "enemy";

/** A resolved frame: the unit + its group + self flag + direct-select hotkey. */
export interface UnitFrameDescriptor {
  readonly entity: WireEntity;
  readonly faction: UnitFaction;
  readonly isSelf: boolean;
  /** Direct-select hotkey label (#136), or null (self / dead / unbound). */
  readonly hotkey: string | null;
}

/** Layout treatment for the unit-frame stack. Every knob is data-driven (#48). */
export interface UnitFrameStackStyle {
  /** Distance from the bottom viewport edge, px. */
  readonly bottomPx: number;
  /** Horizontal inset of each group from its screen edge, px. */
  readonly sideInsetPx: number;
  /** Per-frame width, px. */
  readonly frameWidthPx: number;
  /** Gap between stacked frames within a group, px. */
  readonly frameGapPx: number;
  /** Max major-aura icons per frame. */
  readonly maxIcons: number;
  /** Friendly-frame accent (border + hotkey). */
  readonly friendlyBorderColor: string;
  /** Enemy-frame accent. */
  readonly enemyBorderColor: string;
  readonly row: MajorAuraRowStyle;
}

/** Default treatment: bottom, friendlies-left / enemies-right. */
export const DEFAULT_UNIT_FRAME_STACK_STYLE: UnitFrameStackStyle = {
  bottomPx: 12,
  sideInsetPx: 12,
  frameWidthPx: 150,
  frameGapPx: 6,
  maxIcons: 5,
  friendlyBorderColor: "#3aa675",
  enemyBorderColor: "#e04040",
  row: DEFAULT_MAJOR_AURA_ROW_STYLE,
};

/**
 * Partition the roster into friendly / enemy frame descriptors, each in seat
 * order. Pure — the reducer of the whole stack, exposed so tests drive the
 * grouping + hotkey resolution directly without a Pixi renderer or providers.
 */
export function buildUnitFrames(
  snapshot: WireSimState | null,
  ownSeat: number | null,
  keymap: Keymap,
): {
  readonly friendlies: readonly UnitFrameDescriptor[];
  readonly enemies: readonly UnitFrameDescriptor[];
} {
  if (snapshot === null) return { friendlies: [], enemies: [] };
  const me = ownEntity(snapshot, ownSeat);
  const myTeam = me?.team ?? null;
  const friendlies: UnitFrameDescriptor[] = [];
  const enemies: UnitFrameDescriptor[] = [];
  const sorted = [...snapshot.entities].sort((a, b) => a.seat - b.seat);
  for (const e of sorted) {
    // #744: an unknown viewer team (myTeam === null) must NOT collapse the
    // whole roster to "enemy" — that misclassifies the viewer's own frame
    // as hostile too. Degrade to a neutral single group instead (everyone
    // friendly, nobody enemy) rather than asserting universal hostility.
    const isFriendly = myTeam === null || e.team === myTeam;
    const np = entityNameplate(snapshot, ownSeat ?? -1, keymap, e.entityId);
    const desc: UnitFrameDescriptor = {
      entity: e,
      faction: isFriendly ? "friendly" : "enemy",
      isSelf: ownSeat !== null && e.seat === ownSeat,
      hotkey: np?.label ?? null,
    };
    (isFriendly ? friendlies : enemies).push(desc);
  }
  return { friendlies, enemies };
}

export interface UnitFrameStackProps {
  /** Feature toggle. Defaults to the prefs toggle (#138). */
  readonly enabled?: boolean;
  readonly style?: UnitFrameStackStyle;
}

export function UnitFrameStack({
  enabled,
  style = DEFAULT_UNIT_FRAME_STACK_STYLE,
}: UnitFrameStackProps = {}): ReactElement | null {
  const { state } = useStore();
  const { keymap } = useKeymap();
  const hud = loadHudPrefs();
  const on = enabled ?? hud.unitFrames;
  if (!on) return null;

  const categories = hud.nameplateAuraCategories;
  const now = state.hudSnapshot?.tick ?? 0;
  const { friendlies, enemies } = buildUnitFrames(
    state.hudSnapshot,
    state.ownSeat,
    keymap,
  );

  const container: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: style.bottomPx,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    padding: `0 ${String(style.sideInsetPx)}px`,
    pointerEvents: "none",
  };

  return (
    <div
      data-testid="unit-frame-stack"
      data-friendly-count={String(friendlies.length)}
      data-enemy-count={String(enemies.length)}
      style={container}
    >
      <FrameGroup
        testId="unit-frame-group-friendly"
        align="flex-start"
        frames={friendlies}
        now={now}
        categories={categories}
        style={style}
      />
      <FrameGroup
        testId="unit-frame-group-enemy"
        align="flex-end"
        frames={enemies}
        now={now}
        categories={categories}
        style={style}
      />
    </div>
  );
}

function FrameGroup({
  testId,
  align,
  frames,
  now,
  categories,
  style,
}: {
  readonly testId: string;
  readonly align: "flex-start" | "flex-end";
  readonly frames: readonly UnitFrameDescriptor[];
  readonly now: number;
  readonly categories: readonly AuraCategory[];
  readonly style: UnitFrameStackStyle;
}): ReactElement {
  return (
    <div
      data-testid={testId}
      data-count={String(frames.length)}
      style={{ display: "flex", flexDirection: "column", alignItems: align, gap: style.frameGapPx }}
    >
      {frames.map((f) => (
        <RosterFrame
          key={f.entity.entityId}
          desc={f}
          now={now}
          categories={categories}
          style={style}
        />
      ))}
    </div>
  );
}

function RosterFrame({
  desc,
  now,
  categories,
  style,
}: {
  readonly desc: UnitFrameDescriptor;
  readonly now: number;
  readonly categories: readonly AuraCategory[];
  readonly style: UnitFrameStackStyle;
}): ReactElement {
  const e = desc.entity;
  const accent =
    desc.faction === "friendly" ? style.friendlyBorderColor : style.enemyBorderColor;
  // #419/#420: champion identity color is ADDITIVE, same convention as
  // OwnFrame (#140) — team color (the border above) still signals
  // friend/enemy; the label additionally carries THIS ENTITY'S OWN champion
  // color, resolved from its own wire-carried `championId` (#420) — every
  // frame, not just self.
  const championColor = championColorCss(e.championId);
  return (
    <div
      data-testid="unit-frame"
      data-seat={String(e.seat)}
      data-faction={desc.faction}
      data-self={desc.isSelf ? "true" : "false"}
      data-hp={String(e.hp)}
      data-max-hp={String(e.maxHp)}
      data-hotkey={desc.hotkey ?? ""}
      data-alive={e.alive ? "true" : "false"}
      data-champion-color={championColor}
      style={{
        ...unitFrameStyle(desc.faction === "friendly" ? "left" : "right"),
        width: style.frameWidthPx,
        background: "#161c24",
        border: `1px solid ${accent}`,
        borderRadius: 4,
        padding: 6,
        opacity: e.alive ? 1 : 0.4,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 11,
          marginBottom: 3,
          color: "#cfd8e3",
        }}
      >
        <span style={{ color: championColor }}>
          {desc.isSelf ? "you" : `seat ${String(e.seat)}`}
        </span>
        {desc.hotkey !== null ? (
          <span
            data-testid="unit-frame-hotkey"
            style={{ fontWeight: 700, color: accent }}
          >
            {desc.hotkey}
          </span>
        ) : null}
      </div>
      <HpBar hp={e.hp} maxHp={e.maxHp} shieldAmount={shieldAmount(e.auras)} height={8} testId="unit-frame-hp-bar" />
      <FrameCastBar
        cast={e.cast}
        tick={now}
        championId={e.championId}
        testIdPrefix="unit-frame"
      />
      <div style={{ marginTop: 4 }}>
        <MajorAuraRow
          auras={e.auras}
          now={now}
          categories={categories}
          max={style.maxIcons}
          style={style.row}
          testId="unit-frame-aura-row"
          chipTestId="unit-frame-aura-chip"
        />
      </div>
    </div>
  );
}